import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Trash2,
  Send,
  Mail,
  Info,
} from "lucide-react";
import { DiscrepancyService } from "../../services/discrepancyService";
import { ProjectService } from "../../services/projectService";
import FileUpload from "../FileUpload/FileUpload";
import MultiSelectDropdown from "../MultiSelectDropdown";
import {
  buildStructuredEmail,
} from "../../utils/emailHtmlFormatter";
import { supabase } from "../../lib/supabase";
import { Modal, ModalBody, ModalFooter, Button } from '../ui';
import ContactDetailsModal from '../ContactDetailsModal';
import NewContactModal from '../NewContactModal';
import { formatJobRevNumber } from '../../utils/projectFormatting';
import '../ExportAndSend/ExportForm.css';

const DiscrepancyReviewModal = ({
  discrepancy,
  isOpen,
  onClose,
  onSave,
  onDelete,
}) => {
  const [formData, setFormData] = useState({
    discrepancyType: "",
    discrepancyStatus: "Draft",
    description: "",
    projectId: null,
    loomLink: "",
    markupAttachment: [],
    additionalInformation: "",
    additionalContactIds: [],
    createdBy: "",
    sentDate: null,
    customMessage: "",   // User's optional additions to auto-generated email
    emailHtml: "",       // Generated HTML preview
    emailTo: [],
    emailCc: [],
    emailSubject: "",
    sendEmail: false,
    putProjectOnHold: false,
    holdReason: "waiting_discrepancy_clarification",
    holdNotes: "",
    holdInitialFollowUpDays: 14,
    holdRecurringFollowUpDays: 7,
  });

  // eslint-disable-next-line no-unused-vars
  const [projects, setProjects] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [projectDetails, setProjectDetails] = useState(null);
  const [clientContacts, setClientContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [linkCopied, setLinkCopied] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [createdByName, setCreatedByName] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  // eslint-disable-next-line no-unused-vars
  const projectSearchRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const dropdownRef = useRef(null);
  const htmlUpdateTimerRef = useRef(null);
  const toastTimerRef = useRef(null);

  // Toast notification helper
  const showToast = (message, type = 'success') => {
    // Clear existing timer
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToast({ visible: true, message, type });

    // Auto-hide after 5 seconds
    toastTimerRef.current = setTimeout(() => {
      setToast({ visible: false, message: '', type: 'success' });
    }, 5000);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // Initialize form data when discrepancy changes
  useEffect(() => {
    if (discrepancy) {
      // Load existing discrepancy data - map from actual database fields
      console.log("Loading discrepancy data:", discrepancy);
      console.log("Discrepancy project field:", discrepancy.project);
      console.log("Discrepancy projectId field:", discrepancy.projectId);
      console.log("Discrepancy project_id field:", discrepancy.project_id);
      const projectIdToSet =
        discrepancy.projectId ||
        discrepancy.project_id ||
        discrepancy.project?.id ||
        null;
      console.log("Setting projectId to formData:", projectIdToSet);
      setFormData({
        discrepancyType: discrepancy.discrepancyType || discrepancy.type || "",
        discrepancyStatus:
          discrepancy.discrepancyStatus || discrepancy.status || "Draft",
        description:
          discrepancy.description || discrepancy.additionalInformation || "",
        projectId: projectIdToSet,
        loomLink: discrepancy.loomLink || discrepancy.loom_link || "",
        markupAttachment: (() => {
          const attachment =
            discrepancy.markupAttachment || discrepancy.markup_attachment;
          console.log("Loading markup_attachment from DB:", attachment);
          console.log("Type of attachment:", typeof attachment);

          if (Array.isArray(attachment)) {
            console.log("Attachment is array, length:", attachment.length);
            if (attachment.length > 0) {
              console.log("First attachment item:", attachment[0]);
              console.log("Type of first item:", typeof attachment[0]);
            }
            return attachment;
          } else if (typeof attachment === "string") {
            console.log("Attachment is string, trying to parse as JSON");
            try {
              const parsed = JSON.parse(attachment);
              console.log("Parsed attachment:", parsed);
              return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              console.error("Failed to parse attachment string:", e);
              return [];
            }
          } else {
            console.log("Attachment is neither array nor string");
            return [];
          }
        })(),
        additionalInformation:
          discrepancy.additionalInformation ||
          discrepancy.additional_information ||
          discrepancy.description ||
          "",
        additionalContactIds: discrepancy.additionalContactIds || [],
        createdBy:
          discrepancy.createdByName ||
          discrepancy.created_by_name ||
          discrepancy.createdBy ||
          discrepancy.created_by ||
          "",
        sentDate: discrepancy.sentDate || discrepancy.sent_date || null,
        customMessage:
          discrepancy.customMessage || discrepancy.custom_message || "",
        emailHtml:
          discrepancy.emailHtml || discrepancy.email_html || "",
        emailTo: discrepancy.emailTo || discrepancy.email_to || [],
        emailCc: discrepancy.emailCc || discrepancy.email_cc || [],
        emailSubject:
          discrepancy.emailSubject || discrepancy.email_subject || "",
        sendEmail: discrepancy.sendEmail ?? discrepancy.send_email ?? false,
        putProjectOnHold: discrepancy.putProjectOnHold ?? discrepancy.put_project_on_hold ?? false,
        holdReason: discrepancy.holdReason || discrepancy.hold_reason || "waiting_discrepancy_clarification",
        holdNotes: discrepancy.holdNotes || discrepancy.hold_notes || "",
        holdInitialFollowUpDays: discrepancy.holdInitialFollowUpDays || discrepancy.hold_initial_follow_up_days || 14,
        holdRecurringFollowUpDays: discrepancy.holdRecurringFollowUpDays || discrepancy.hold_recurring_follow_up_days || 7,
      });

      // Load project details if we have a project
      const projectId =
        discrepancy.projectId ||
        discrepancy.project_id ||
        discrepancy.project?.id;
      if (projectId) {
        loadProjectDetails(projectId);
      }

      setIsDirty(false);
    }
  }, [discrepancy]);

  // Load project details and contacts
  const loadProjectDetails = async (projectId) => {
    try {
      const projectData =
        await DiscrepancyService.getProjectWithContacts(projectId);
      setProjectDetails(projectData);

      if (projectData.client_id) {
        const contacts = await DiscrepancyService.getContactsByClientId(
          projectData.client_id,
        );
        setClientContacts(contacts);
      }
      
      // Set email "to" field with project contact if available and emailTo is empty
      if (projectData?.contact_id && formData.emailTo.length === 0) {
        setFormData((prev) => ({
          ...prev,
          emailTo: [projectData.contact_id],
        }));
      }
    } catch (err) {
      console.error("Error loading project details:", err);
    }
  };

  // Projects list not needed for review modal - project is already selected

  // Load current user and email templates
  useEffect(() => {
    const loadUserAndTemplates = async () => {
      try {
        // Load current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;

        if (user) {
          const { data: userData, error: profileError } = await supabase
            .from('employees')
            .select('*')
            .eq('email', user.email)
            .single();

          if (!profileError && userData) {
            setCurrentUser(userData);
          }
        }

        // Load email templates
        const { data: templates, error: templatesError } = await supabase
          .from('discrepancy_and_clarification_type')
          .select('*')
          .order('name');

        if (templatesError) throw templatesError;
        setEmailTemplates(templates || []);
      } catch (error) {
        console.error('Error loading user and templates:', error);
      }
    };

    if (isOpen) {
      loadUserAndTemplates();
    }
  }, [isOpen]);

  // Look up creator name from email
  useEffect(() => {
    const lookupCreatorName = async () => {
      if (!formData.createdBy) return;

      // Check if createdBy is an email (contains @)
      if (formData.createdBy.includes('@')) {
        try {
          const { data, error } = await supabase
            .from('employees')
            .select('name')
            .eq('email', formData.createdBy)
            .single();

          if (!error && data?.name) {
            setCreatedByName(data.name);
          } else {
            // If not found in employees, use email
            setCreatedByName(formData.createdBy);
          }
        } catch (err) {
          console.error('Error looking up creator name:', err);
          setCreatedByName(formData.createdBy);
        }
      } else {
        // Already a name, use as is
        setCreatedByName(formData.createdBy);
      }
    };

    lookupCreatorName();
  }, [formData.createdBy]);

  // Auto-regenerate HTML when customMessage or discrepancies change (debounced)
  useEffect(() => {
    if (formData.sendEmail !== 'yes') return;

    // Clear existing timer
    if (htmlUpdateTimerRef.current) {
      clearTimeout(htmlUpdateTimerRef.current);
    }

    // Set new timer for 1.5 seconds
    htmlUpdateTimerRef.current = setTimeout(() => {
      // Regenerate email HTML using buildStructuredEmail
      handleGenerateEmail();
    }, 1500);

    return () => {
      if (htmlUpdateTimerRef.current) {
        clearTimeout(htmlUpdateTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customMessage, formData.loomLink, formData.markupAttachment, formData.discrepancyType, formData.sendEmail]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Load project details when project changes
    if (name === "projectId" && value) {
      handleProjectSelect(value);
    } else if (name === "projectId" && !value) {
      setProjectDetails(null);
      setClientContacts([]);
    }

    setIsDirty(true);
  };

  // Handle project selection
  const handleProjectSelect = async (projectId) => {
    const selectedProject = projects.find((p) => p.id === projectId);
    console.log("=== PROJECT SELECTION DEBUG ===");
    console.log("Selected projectId:", projectId);
    console.log("Found project:", selectedProject);
    console.log("Project client_id field:", selectedProject?.client_id);
    console.log("Project clients object:", selectedProject?.clients);
    console.log("Project clients.id:", selectedProject?.clients?.id);

    if (selectedProject) {
      // Set project details - ensure client_id is properly set
      const projectDetailsToSet = {
        ...selectedProject,
        client_id: selectedProject.client_id || selectedProject.clients?.id,
      };
      console.log(
        "Setting projectDetails with client_id:",
        projectDetailsToSet.client_id,
      );
      console.log("Full projectDetails object:", projectDetailsToSet);
      setProjectDetails(projectDetailsToSet);

      // Load contacts for the client
      const clientId = selectedProject.client_id || selectedProject.clients?.id;
      console.log("Client ID for loading contacts:", clientId);
      if (clientId) {
        try {
          const contacts =
            await DiscrepancyService.getContactsByClientId(clientId);
          setClientContacts(contacts);
        } catch (err) {
          console.error("Error loading contacts:", err);
        }
      }

      // Get full project details to ensure we have contact_id
      try {
        const projectData = await DiscrepancyService.getProjectWithContacts(projectId);
        
        // Set email "to" field with project contact if available
        let emailTo = formData.emailTo;
        if (projectData?.contact_id) {
          emailTo = [projectData.contact_id];
        }

        setFormData((prev) => ({
          ...prev,
          projectId: projectId,
          emailTo: emailTo,
        }));
        
        // Update project details with full data
        setProjectDetails(projectData);
      } catch (err) {
        console.error("Error loading project details:", err);
        // Fallback to just setting project ID if detailed load fails
        setFormData((prev) => ({
          ...prev,
          projectId: projectId,
        }));
      }

      // Update search term to show selected project
      setProjectSearchTerm(formatProjectDisplay(selectedProject));
      setShowProjectDropdown(false);
      setIsDirty(true);
    }
  };

  // Filter projects based on search term - comprehensive search like GlobalSearch
  useEffect(() => {
    if (!projectSearchTerm) {
      setFilteredProjects(projects);
      return;
    }

    const searchLower = projectSearchTerm.toLowerCase().trim();

    // Comprehensive filtering that matches global search functionality
    const filtered = projects.filter((project) => {
      // Search in project name
      const projectName = (project.name || "").toLowerCase();

      // Search in job number (including rev formatting)
      const jobNumber = (project.job_number || "").toLowerCase();
      const formattedJobRev = formatJobRevNumber(
        project.job_number,
        project.rev,
      ).toLowerCase();

      // Search in client name
      const clientName = (project.clients?.name || "").toLowerCase();

      // Search in contact name and email
      const contactName = (project.contacts?.name || "").toLowerCase();
      const contactEmail = (project.contacts?.email || "").toLowerCase();

      return (
        projectName.includes(searchLower) ||
        jobNumber.includes(searchLower) ||
        formattedJobRev.includes(searchLower) ||
        clientName.includes(searchLower) ||
        contactName.includes(searchLower) ||
        contactEmail.includes(searchLower)
      );
    });

    // Sort filtered results by job number (highest to lowest) like global search
    const sortedFiltered = filtered.sort((a, b) => {
      const parseJobNumber = (jobNum) => {
        if (!jobNum) return 0;
        const parts = jobNum.split("-");
        if (parts.length === 2) {
          const year = parseInt(parts[0]) || 0;
          const num = parseInt(parts[1]) || 0;
          return year * 10000 + num;
        }
        return parseInt(jobNum) || 0;
      };

      const numA = parseJobNumber(a.job_number);
      const numB = parseJobNumber(b.job_number);
      return numB - numA; // Descending order (higher numbers first)
    });

    setFilteredProjects(sortedFiltered);
  }, [projectSearchTerm, projects]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
    };

    if (showProjectDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showProjectDropdown]);

  // Handle file uploads
  const handleFilesChange = useCallback(
    async (newFiles) => {
      console.log("DiscrepancyModal: handleFilesChange called with:", newFiles);
      setFormData((prev) => {
        const updated = {
          ...prev,
          markupAttachment: newFiles,
        };
        console.log(
          "DiscrepancyModal: Updated formData.markupAttachment:",
          updated.markupAttachment,
        );
        return updated;
      });
      setIsDirty(true);

      // Auto-save when files are changed
      if (discrepancy) {
        try {
          // Get current form data at the time of file change
          setFormData((currentFormData) => {
            const updatedData = {
              ...currentFormData,
              markupAttachment: newFiles,
            };

            // Perform auto-save asynchronously without affecting form state
            setTimeout(async () => {
              try {
                const savedDiscrepancy = await DiscrepancyService.updateDiscrepancy(
                  discrepancy.id,
                  updatedData,
                );
                console.log("Auto-saved discrepancy after file change");

                // Update parent state so files appear when modal reopens
                if (onSave && savedDiscrepancy) {
                  await onSave(savedDiscrepancy, false); // closeModal=false
                }
              } catch (err) {
                console.error("Error auto-saving after file change:", err);
              }
            }, 100);

            return updatedData;
          });
        } catch (err) {
          console.error("Error preparing auto-save after file change:", err);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [discrepancy],
  );

  // handleSendToReview removed - that's for Draft status (handled by simple modal)

  // Handle accept (only for non-Draft status)
  const handleAccept = async () => {
    // Validate required fields
    if (!formData.discrepancyType) {
      setError("Discrepancy type is required");
      return;
    }

    if (!formData.projectId) {
      setError("Project selection is required");
      return;
    }

    if (!formData.sendEmail) {
      setError("Please select whether to send email or not");
      return;
    }

    if (!formData.putProjectOnHold) {
      setError("Please select whether to put project on hold or not");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (formData.sendEmail === "no") {
        // Just change status to "Reviewed - not sent"
        const updatedFormData = {
          ...formData,
          discrepancyStatus: "Reviewed - not sent",
        };
        await onSave(updatedFormData);
        setFormData(updatedFormData);
        setIsDirty(false);
      } else if (formData.sendEmail === "yes") {
        // Send email and change status to "Sent"
        if (!formData.emailHtml) {
          setError("Please prepare an email first");
          return;
        }

        if (!discrepancy?.id) {
          setError("Cannot send email for unsaved discrepancy");
          return;
        }

        // Build email preview text (for database storage/backwards compatibility)
        // This is auto-generated, not editable by user
        const emailPreviewText = `Email sent for ${formData.discrepancyType}`;

        // Send the email
        const updatedDiscrepancy =
          await DiscrepancyService.sendDiscrepancyEmail(
            discrepancy.id,
            emailPreviewText,  // Plain text preview for database
            formData.emailTo,
            formData.emailCc,
            formData.emailSubject,
            formData.emailHtml,  // Actual HTML content to send
          );

        // If put project on hold is enabled, update project status
        if (formData.putProjectOnHold === "yes" && formData.projectId) {
          try {
            // When both send email and hold are "yes", save email recipients to hold fields too
            const holdUpdateData = {
              modeling_state: "Hold",
              hold_reason: formData.holdReason,
              hold_notes: formData.holdNotes,
              hold_initial_follow_up_days: formData.holdInitialFollowUpDays,
              hold_recurring_follow_up_days: formData.holdRecurringFollowUpDays,
              hold_start_date: new Date().toISOString(),
              hold_email_to: formData.emailTo,
              hold_email_cc: formData.emailCc,
            };

            await ProjectService.updateProjectBatch(
              formData.projectId,
              holdUpdateData,
            );
          } catch (holdError) {
            console.error("Error putting project on hold:", holdError);
            setError(
              "Email sent but failed to put project on hold: " +
                (holdError.message || "Unknown error"),
            );
          }
        }

        // Update the local form data with the new status
        const updatedFormData = {
          ...formData,
          discrepancyStatus: "Sent",
          sentDate: updatedDiscrepancy.sent_date || new Date().toISOString(),
        };
        setFormData(updatedFormData);

        // Show success message with toast
        const successMessage =
          formData.putProjectOnHold === "yes"
            ? "Discrepancy email sent successfully! Project has been put on hold."
            : "Discrepancy email sent successfully!";
        showToast(successMessage, 'success');

        // Delay refresh and close to show the toast notification
        setTimeout(async () => {
          // Refresh the parent component's data
          if (onSave) {
            await onSave(updatedFormData);
          }

          // Close the modal
          if (onClose) {
            onClose();
          }
        }, 4000); // 4 second delay to show toast before closing
      }

      // Handle project on hold when send email is "no"
      if (
        formData.putProjectOnHold === "yes" &&
        formData.sendEmail === "no" &&
        formData.projectId
      ) {
        try {
          const holdUpdateData = {
            modeling_state: "Hold",
            hold_reason: formData.holdReason,
            hold_notes: formData.holdNotes,
            hold_initial_follow_up_days: formData.holdInitialFollowUpDays,
            hold_recurring_follow_up_days: formData.holdRecurringFollowUpDays,
            hold_start_date: new Date().toISOString(),
            hold_email_to: formData.emailTo,
            hold_email_cc: formData.emailCc,
          };

          await ProjectService.updateProjectBatch(
            formData.projectId,
            holdUpdateData,
          );
        } catch (holdError) {
          console.error("Error putting project on hold:", holdError);
          setError(
            "Failed to put project on hold: " +
              (holdError.message || "Unknown error"),
          );
        }
      }
    } catch (err) {
      setError(err.message || "Failed to accept discrepancy");
    } finally {
      setLoading(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    // Validate required fields
    if (!formData.discrepancyType) {
      setError("Discrepancy type is required");
      return;
    }

    if (!formData.projectId) {
      setError("Project selection is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Include emailPreview in save data if it exists
      // This allows saving prepared emails before sending
      await onSave(formData);
      setIsDirty(false);
      // Don't close modal after save - keep it open
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to save discrepancy");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!discrepancy) return;

    setLoading(true);
    try {
      await onDelete(discrepancy.id);
    } catch (err) {
      setError(err.message || "Failed to delete discrepancy");
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    onClose();
  };

  // Handle overlay click - only close if clicking the actual overlay, not modal content
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle escape key - only close if not typing in an input field
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        // Check if user is currently typing in an input field
        const activeElement = document.activeElement;
        const isTyping =
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.isContentEditable);

        // Only close modal if not typing in a field
        if (!isTyping) {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Dropdown height fix removed - not needed with new standardized UI

  // Auto-prepare email when sendEmail or putProjectOnHold changes (using structured email builder)
  useEffect(() => {
    const autoPrepareEmail = async () => {
      // Generate email if sendEmail is "yes" OR if putProjectOnHold is "yes"
      const shouldGenerateEmail =
        formData.sendEmail === "yes" || formData.putProjectOnHold === "yes";

      if (shouldGenerateEmail && formData.discrepancyType && emailTemplates.length > 0) {
        // Trigger the handleGenerateEmail function which now uses buildStructuredEmail
        handleGenerateEmail();
      }
    };

    autoPrepareEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.sendEmail,
    formData.discrepancyType,
    formData.putProjectOnHold,
    formData.holdInitialFollowUpDays,
    formData.emailTo,  // Regenerate when To: field changes (to update greeting)
    projectDetails,
    clientContacts,
    emailTemplates,
  ]);

  // Auto-fill email recipients when Send Email changes to 'yes'
  useEffect(() => {
    if (formData.sendEmail === "yes") {
      // Auto-fill the email preview To/CC fields with the left side selections
      // This ensures the email fields match what's already selected for follow-up emails
      // Note: The email preview fields are managed by the auto-prepare email useEffect
    }
  }, [formData.sendEmail]);

  // Handle generating share link
  // eslint-disable-next-line no-unused-vars
  const handleGenerateShareLink = async () => {
    if (!discrepancy?.id) {
      setError("Cannot generate link - discrepancy not saved yet");
      return;
    }

    try {
      const shareUrl = `${window.location.origin}/discrepancies/${discrepancy.id}`;

      // Save the share URL to the database
      await DiscrepancyService.updateShareUrl(discrepancy.id, shareUrl);

      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);

      // Reset the copied indicator after 2 seconds
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Failed to generate share link:", err);
      setError(
        "Failed to generate share link: " + (err.message || "Unknown error"),
      );
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'Startup': '#6b7280',
      'Data Prep': '#3b82f6',
      'Ready to Model': '#8b5cf6',
      'Modeling': '#f59e0b',
      'Double Check': '#ec4899',
      'Corrections': '#f97316',
      'Review': '#14b8a6',
      'Ready to Export': '#10b981',
      'Completed': '#22c55e',
      'Utilities': '#64748b'
    };
    return statusColors[status] || '#6b7280';
  };

  // Format project display
  const formatProjectDisplay = (project) => {
    if (!project) return "";
    let display = "";
    const jobRevNumber = formatJobRevNumber(project.job_number, project.rev);
    if (jobRevNumber) {
      display = jobRevNumber + " - ";
    }
    display += project.name || "Unnamed Project";
    return display;
  };

  // Generate discrepancy email with structured HTML builder
  const handleGenerateEmail = async () => {
    if (!formData.discrepancyType) return;

    setGeneratingEmail(true);
    try {
      const template = emailTemplates.find(t => t.name === formData.discrepancyType);
      if (!template) return;

      // Get contact first name for personalized greeting
      const toContactId = formData.emailTo && formData.emailTo.length > 0
        ? formData.emailTo[0]
        : projectDetails?.contact_id;

      const primaryContact = clientContacts.find(contact =>
        toContactId && contact.id === toContactId
      );
      const contactName = primaryContact?.name || "there";
      const contactFirstName = contactName.split(' ')[0] || contactName;

      // Build discrepancy details: video link and/or markup file links
      const structuredDiscrepancies = [];
      const hasLoomLink = formData.loomLink && formData.loomLink.trim();
      const filesWithUrls = (formData.markupAttachment || []).filter(file => {
        const fileUrl = file.url || file.file_url || file.publicUrl || file.public_url || file.direct_download_url || file.signed_url || '';
        return fileUrl && fileUrl.trim() && fileUrl.startsWith('http');
      });

      // Only add discrepancy section if we have video or files
      if (hasLoomLink || filesWithUrls.length > 0) {
        structuredDiscrepancies.push({
          type: formData.discrepancyType,
          videoUrl: hasLoomLink ? formData.loomLink : '',
          fileLinks: filesWithUrls.map(file => ({
            url: file.url || file.file_url || file.publicUrl || file.public_url || file.direct_download_url || file.signed_url,
            filename: file.filename || file.original_filename || file.name || 'Markup File'
          }))
        });
      }

      // Build email sections separately for proper ordering
      const templateMessages = [template.email_body_text];

      const holdNoticeMessages = [];
      if (formData.putProjectOnHold === "yes") {
        // Get project name for personalization
        const projectName = projectDetails?.name || "This project";

        holdNoticeMessages.push(
          `${projectName} is being placed on hold pending a resolution to this concern.`
        );
        holdNoticeMessages.push(
          `We will send a reminder email in ${formData.holdInitialFollowUpDays} days.`
        );
      }

      // Generate discrepancy email HTML
      const htmlContent = buildStructuredEmail({
        greeting: `${contactFirstName},`,
        templateMessages: templateMessages,
        discrepancies: structuredDiscrepancies,
        holdNoticeMessages: holdNoticeMessages,
        customMessage: formData.customMessage || "",
        signature: currentUser?.email_signature_html || "",
      });

      // Auto-populate To field with project contact if empty
      let emailTo = formData.emailTo;
      if (emailTo.length === 0 && projectDetails?.contact_id) {
        emailTo = [projectDetails.contact_id];
      }

      // Auto-generate subject line: "Project Name - Discrepancy Type"
      const projectName = projectDetails?.name || "Project";
      const emailSubject = `${projectName} - ${formData.discrepancyType}`;

      setFormData(prev => ({
        ...prev,
        emailHtml: htmlContent,
        emailTo: emailTo,
        emailSubject: emailSubject,
      }));

      setIsDirty(true);
    } catch (error) {
      console.error('Error generating email:', error);
      setError('Failed to generate email: ' + error.message);
    } finally {
      setGeneratingEmail(false);
    }
  };


  // Auto-generate email when Send Email is "yes" and dependencies change
  useEffect(() => {
    if (formData.sendEmail === 'yes' && formData.discrepancyType && emailTemplates.length > 0) {
      handleGenerateEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.sendEmail, formData.discrepancyType, formData.loomLink, formData.markupAttachment]);


  return (
    <>
    <Modal trackAs="DiscrepancyReviewModal" isOpen={isOpen} onClose={handleClose} size={formData.sendEmail === 'yes' ? 'xl' : 'lg'}>
      {/* Custom Header */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {projectDetails ? (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
                <span
                  style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-400)',
                    flexShrink: 0,
                  }}
                >
                  {formatJobRevNumber(projectDetails.job_number, projectDetails.rev)}
                </span>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-50)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {projectDetails.name}
                </h1>
              </div>
              <p
                style={{
                  margin: 0,
                  marginTop: 'var(--space-1)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}
              >
                {formData.discrepancyType || 'Discrepancy'} - {formData.discrepancyStatus}
              </p>
            </>
          ) : (
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
              }}
            >
              Review Discrepancy
            </h1>
          )}
        </div>
      </div>

      <ModalBody>
        {/* Error message */}
        {error && (
          <div
            style={{
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        {/* Project Information Card */}
        {projectDetails && (
          <div
            style={{
              padding: 'var(--space-4)',
              marginBottom: 'var(--space-6)',
              backgroundColor: 'var(--dark-elevated)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-1)' }}>
                  Project
                </div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)', fontWeight: 'var(--font-medium)' }}>
                  {formatJobRevNumber(projectDetails.job_number, projectDetails.rev)} - {projectDetails.name}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-1)' }}>
                  Client
                </div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)', fontWeight: 'var(--font-medium)' }}>
                  {projectDetails.clients?.name || 'Unknown'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-1)' }}>
                  Primary Contact
                </div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)', fontWeight: 'var(--font-medium)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  {projectDetails.contacts && (
                    <button
                      onClick={() => setShowContactModal(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--gray-400)',
                        transition: 'color var(--transition-fast)',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cta-blue)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--gray-400)'}
                      title="View contact details"
                    >
                      <Info size={16} />
                    </button>
                  )}
                  <span>
                    {projectDetails.contacts?.name || 'No Contact'}
                    {projectDetails.contacts?.email && (
                      <span style={{ color: 'var(--gray-400)', marginLeft: 'var(--space-2)' }}>
                        ({projectDetails.contacts.email})
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-1)' }}>
                  Modeling Status
                </div>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'white',
                    backgroundColor: getStatusColor(projectDetails.modeling_status),
                  }}
                >
                  {projectDetails.modeling_status || 'No Status'}
                </div>
              </div>
            </div>
          </div>
        )}

          {/* Discrepancy Details Section */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h2
              style={{
                margin: 0,
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
              }}
            >
              Discrepancy Details
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
              {/* Type - Display only */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}
                >
                  Type
                </label>
                <div
                  style={{
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--gray-200)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  {formData.discrepancyType || 'Not specified'}
                </div>
              </div>

              {/* Status - Display only */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}
                >
                  Status
                </label>
                <div
                  style={{
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--gray-200)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  {formData.discrepancyStatus}
                </div>
              </div>
            </div>

            {/* Loom Link and Additional Information */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              {/* Loom Link - Display only */}
              {formData.loomLink && (
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--gray-300)',
                    }}
                  >
                    Loom Link
                  </label>
                  <a
                    href={formData.loomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--cta-blue)',
                      fontSize: 'var(--text-sm)',
                      textDecoration: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    {formData.loomLink}
                  </a>
                </div>
              )}

              {/* Additional Information - Display only */}
              {formData.additionalInformation && (
                <div style={{ gridColumn: formData.loomLink ? 'auto' : '1 / -1' }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--gray-300)',
                    }}
                  >
                    Description
                  </label>
                  <div
                    style={{
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {formData.additionalInformation}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* File Upload Section - Two Column Layout */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <div
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--gray-300)',
                marginBottom: 'var(--space-3)',
              }}
            >
              File Attachments
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              {/* Left: Upload Box */}
              <div>
                <FileUpload
                  files={formData.markupAttachment}
                  onFilesChange={handleFilesChange}
                  projectId={formData.projectId}
                  clientId={projectDetails?.client_id}
                  disabled={loading}
                  maxFiles={10}
                  showFileList={false}
                />
              </div>

              {/* Right: Uploaded Files List */}
              <div>
                {formData.markupAttachment.length > 0 ? (
                  <>
                    <div style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--gray-400)',
                      marginBottom: 'var(--space-2)',
                      textTransform: 'uppercase'
                    }}>
                      Uploaded Files ({formData.markupAttachment.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                      {formData.markupAttachment.map((file, index) => (
                        <div
                          key={file.id || index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 'var(--space-2)',
                            backgroundColor: 'var(--dark-elevated)',
                            border: '1px solid var(--gray-700)',
                            borderRadius: 'var(--radius-md)',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--gray-600)';
                            e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--gray-700)';
                            e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                          }}
                        >
                          <div style={{
                            fontSize: 'var(--text-sm)',
                            color: 'var(--gray-200)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0
                          }}>
                            {file.filename || file.original_filename || file.name}
                          </div>
                          {file.url && (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: 'var(--text-sm)',
                                color: 'var(--cta-blue)',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                fontWeight: 'var(--font-medium)',
                                marginLeft: 'var(--space-2)',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.textDecoration = 'underline';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.textDecoration = 'none';
                              }}
                            >
                              View
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{
                    padding: 'var(--space-4)',
                    textAlign: 'center',
                    color: 'var(--gray-500)',
                    fontSize: 'var(--text-sm)',
                    border: '1px dashed var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                  }}>
                    No files uploaded yet
                  </div>
                )}
              </div>
            </div>
          </div>

            {/* Put Project on Hold Toggle (not shown for Draft status) */}
            {formData.discrepancyStatus !== "Draft" && (
              <div style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--gray-300)' }}>
                    Put Project on Hold <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <div style={{
                    display: 'inline-flex',
                    padding: '2px',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    width: 'fit-content'
                  }}>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, putProjectOnHold: 'yes' }))}
                      style={{
                        padding: '6px 20px',
                        border: 'none',
                        borderRadius: 'calc(var(--radius-md) - 2px)',
                        backgroundColor: formData.putProjectOnHold === 'yes' ? 'var(--cta-blue)' : 'transparent',
                        color: formData.putProjectOnHold === 'yes' ? 'white' : 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.putProjectOnHold !== 'yes') {
                          e.currentTarget.style.color = 'var(--gray-200)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.putProjectOnHold !== 'yes') {
                          e.currentTarget.style.color = 'var(--gray-400)';
                        }
                      }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, putProjectOnHold: 'no' }))}
                      style={{
                        padding: '6px 20px',
                        border: 'none',
                        borderRadius: 'calc(var(--radius-md) - 2px)',
                        backgroundColor: formData.putProjectOnHold === 'no' ? 'var(--cta-blue)' : 'transparent',
                        color: formData.putProjectOnHold === 'no' ? 'white' : 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.putProjectOnHold !== 'no') {
                          e.currentTarget.style.color = 'var(--gray-200)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.putProjectOnHold !== 'no') {
                          e.currentTarget.style.color = 'var(--gray-400)';
                        }
                      }}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Hold Details - show when putProjectOnHold='yes' */}
            {formData.discrepancyStatus !== "Draft" &&
              formData.putProjectOnHold === "yes" && (
                <div style={{ marginBottom: 'var(--space-6)' }}>
                  <h2
                    style={{
                      margin: 0,
                      marginBottom: 'var(--space-4)',
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)',
                    }}
                  >
                    Project Hold Details
                  </h2>

                  {/* Hold Reason - Full width */}
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Hold Reason
                    </label>
                    <div
                      style={{
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                      }}
                    >
                      Waiting on Discrepancy/Clarification
                    </div>
                  </div>

                  {/* Additional Notes - Full width */}
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <label
                      htmlFor="holdNotes"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Additional Notes
                    </label>
                    <textarea
                      id="holdNotes"
                      name="holdNotes"
                      value={formData.holdNotes}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          holdNotes: e.target.value,
                        }));
                        setIsDirty(true);
                      }}
                      placeholder="Add any additional context or details about the hold..."
                      rows="3"
                      style={{
                        width: '100%',
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-700)'}
                    />
                  </div>

                  {/* Follow-up Schedule - Compact inline layout */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-3)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Follow-up Schedule
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        flexWrap: 'wrap',
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                        Send first reminder in
                      </span>
                      <input
                        id="holdInitialFollowUpDays"
                        type="number"
                        min="1"
                        max="365"
                        value={formData.holdInitialFollowUpDays}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            holdInitialFollowUpDays: parseInt(e.target.value) || 14,
                          }));
                          setIsDirty(true);
                        }}
                        style={{
                          width: '80px',
                          padding: 'var(--space-2)',
                          backgroundColor: 'var(--dark-bg)',
                          border: '1px solid var(--gray-600)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--gray-200)',
                          fontSize: 'var(--text-sm)',
                          fontFamily: 'inherit',
                          textAlign: 'center',
                          transition: 'border-color var(--transition-fast)',
                        }}
                        onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                        onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-600)'}
                      />
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                        days, then every
                      </span>
                      <input
                        id="holdRecurringFollowUpDays"
                        type="number"
                        min="1"
                        max="365"
                        value={formData.holdRecurringFollowUpDays}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            holdRecurringFollowUpDays: parseInt(e.target.value) || 7,
                          }));
                          setIsDirty(true);
                        }}
                        style={{
                          width: '80px',
                          padding: 'var(--space-2)',
                          backgroundColor: 'var(--dark-bg)',
                          border: '1px solid var(--gray-600)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--gray-200)',
                          fontSize: 'var(--text-sm)',
                          fontFamily: 'inherit',
                          textAlign: 'center',
                          transition: 'border-color var(--transition-fast)',
                        }}
                        onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                        onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-600)'}
                      />
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                        days after
                      </span>
                    </div>
                  </div>
                </div>
              )}

            {/* Send Email Toggle (not shown for Draft status) - Disabled until Hold selection made */}
            {formData.discrepancyStatus !== "Draft" && (
              <div style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--gray-300)' }}>
                    Send Email <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <div style={{
                    display: 'inline-flex',
                    padding: '2px',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    width: 'fit-content',
                    opacity: !formData.putProjectOnHold ? 0.5 : 1,
                    pointerEvents: !formData.putProjectOnHold ? 'none' : 'auto'
                  }}>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, sendEmail: 'yes' }))}
                      disabled={!formData.putProjectOnHold}
                      style={{
                        padding: '6px 20px',
                        border: 'none',
                        borderRadius: 'calc(var(--radius-md) - 2px)',
                        backgroundColor: formData.sendEmail === 'yes' ? 'var(--cta-blue)' : 'transparent',
                        color: formData.sendEmail === 'yes' ? 'white' : 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: !formData.putProjectOnHold ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.sendEmail !== 'yes' && formData.putProjectOnHold) {
                          e.currentTarget.style.color = 'var(--gray-200)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.sendEmail !== 'yes') {
                          e.currentTarget.style.color = 'var(--gray-400)';
                        }
                      }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, sendEmail: 'no' }))}
                      disabled={!formData.putProjectOnHold}
                      style={{
                        padding: '6px 20px',
                        border: 'none',
                        borderRadius: 'calc(var(--radius-md) - 2px)',
                        backgroundColor: formData.sendEmail === 'no' ? 'var(--cta-blue)' : 'transparent',
                        color: formData.sendEmail === 'no' ? 'white' : 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: !formData.putProjectOnHold ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.sendEmail !== 'no' && formData.putProjectOnHold) {
                          e.currentTarget.style.color = 'var(--gray-200)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.sendEmail !== 'no') {
                          e.currentTarget.style.color = 'var(--gray-400)';
                        }
                      }}
                    >
                      No
                    </button>
                  </div>
                  {!formData.putProjectOnHold && (
                    <small
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--gray-500)',
                        fontStyle: 'italic'
                      }}
                    >
                      Please select "Put Project on Hold" first
                    </small>
                  )}
                </div>
              </div>
            )}

            {/* Email Recipients Section (show after Send Email selection is made) */}
            {formData.discrepancyStatus !== "Draft" &&
              formData.sendEmail !== "" && (
                <div className="export-form-section">
                  <h3>
                    {formData.sendEmail === "yes"
                      ? "Email Recipients"
                      : "Hold Notification Recipients"}
                  </h3>
                  <div className="export-recipients-section">
                    <p style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--gray-400)',
                      margin: '0 0 var(--space-4) 0'
                    }}>
                      {formData.sendEmail === "yes"
                        ? (formData.putProjectOnHold === "yes"
                            ? "Select contacts for discrepancy email and hold notifications"
                            : "Select contacts for discrepancy email")
                        : "Select contacts for hold follow-up notifications"}
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                    {/* Left: To and CC Fields Stacked */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                      {/* Email To Field */}
                      <div className="export-email-field">
                        <label className="export-email-field-label">
                          To: <span className="required">*</span>
                        </label>
                        <div className="export-email-field-input">
                          <MultiSelectDropdown
                            options={clientContacts.map(
                              (contact) =>
                                contact.name +
                                (contact.email ? ` (${contact.email})` : ""),
                            )}
                            selected={(formData.emailTo || [])
                              .map((id) => {
                                const contact = clientContacts.find(
                                  (c) => c.id === id,
                                );
                                return contact
                                  ? contact.name +
                                      (contact.email ? ` (${contact.email})` : "")
                                  : "";
                              })
                              .filter(Boolean)}
                            onChange={(selectedNames) => {
                              const selectedIds = selectedNames
                                .map((name) => {
                                  const contact = clientContacts.find(
                                    (c) =>
                                      c.name +
                                        (c.email ? ` (${c.email})` : "") ===
                                      name,
                                  );
                                  return contact ? contact.id : null;
                                })
                                .filter(Boolean);
                              setFormData((prev) => ({
                                ...prev,
                                emailTo: selectedIds,
                              }));
                              setIsDirty(true);
                            }}
                            placeholder="Select email recipients..."
                          />
                        </div>
                      </div>

                      {/* Email CC Field */}
                      <div className="export-email-field">
                        <label className="export-email-field-label">CC:</label>
                        <div className="export-email-field-input">
                          <MultiSelectDropdown
                            options={clientContacts
                              .filter(
                                (contact) =>
                                  !formData.emailTo.includes(contact.id),
                              )
                              .map(
                                (contact) =>
                                  contact.name +
                                  (contact.email ? ` (${contact.email})` : ""),
                              )}
                            selected={(formData.emailCc || [])
                              .map((id) => {
                                const contact = clientContacts.find(
                                  (c) => c.id === id,
                                );
                                return contact
                                  ? contact.name +
                                      (contact.email ? ` (${contact.email})` : "")
                                  : "";
                              })
                              .filter(Boolean)}
                            onChange={(selectedNames) => {
                              const selectedIds = selectedNames
                                .map((name) => {
                                  const contact = clientContacts.find(
                                    (c) =>
                                      c.name +
                                        (c.email ? ` (${c.email})` : "") ===
                                      name,
                                  );
                                  return contact ? contact.id : null;
                                })
                                .filter(Boolean);
                              setFormData((prev) => ({
                                ...prev,
                                emailCc: selectedIds,
                              }));
                              setIsDirty(true);
                            }}
                            placeholder="Add CC recipients (optional)..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Right: Create New Contact Button */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowNewContactModal(true);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-4)',
                          background: 'var(--cta-orange)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        + New Contact
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              )}

            {/* Email Content Section - Full width when sendEmail is 'yes' */}
            {formData.discrepancyStatus !== "Draft" &&
              formData.sendEmail === "yes" && (
                <div className="export-form-section">
                  <h3>Email Content</h3>

                  {/* Subject Field */}
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <label
                      htmlFor="emailSubject"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="emailSubject"
                      name="emailSubject"
                      value={formData.emailSubject}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, emailSubject: e.target.value }));
                        setIsDirty(true);
                      }}
                      placeholder="Email subject..."
                      style={{
                        width: '100%',
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-700)'}
                    />
                  </div>

                  {/* Two-column layout: Custom Message + Preview */}
                  <div className="export-email-container">
                    {/* Left Side - Email Editor with Custom Message */}
                    <div className="export-email-editor">
                      <div className="export-email-editor-header">
                        <h4>Custom Message (Optional)</h4>
                        <small>Add additional notes to the auto-generated email</small>
                      </div>
                      <textarea
                        id="customMessage"
                        name="customMessage"
                        value={formData.customMessage}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, customMessage: e.target.value }));
                          setIsDirty(true);
                        }}
                        placeholder="Add any custom notes here (optional)..."
                        className="export-email-body"
                      />
                    </div>

                    {/* Right Side - HTML Preview */}
                    <div className="export-email-preview">
                      <div className="export-email-preview-header">
                        <h4>Email Preview</h4>
                        <small>How the email will appear</small>
                      </div>
                      <div className="export-email-preview-content">
                        <iframe
                          srcDoc={formData.emailHtml || '<div style="padding: 20px; text-align: center; color: #666;">Email preview will appear here</div>'}
                          title="Email Preview"
                          className="export-email-preview-iframe"
                          sandbox="allow-same-origin allow-scripts"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

        {/* Metadata Footer */}
        {discrepancy && (
          <div
            style={{
              marginTop: 'var(--space-6)',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--gray-700)',
              fontSize: 'var(--text-xs)',
              color: 'var(--gray-500)',
            }}
          >
            Created: {formatDate(discrepancy.createdAt || discrepancy.created_at)} by {createdByName || formData.createdBy || 'Unknown'}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {/* Left side - Delete button */}
          <Button variant="danger" onClick={handleDelete} disabled={loading}>
            <Trash2 size={14} />
            Delete
          </Button>

          {/* Right side - Accept button */}
          {formData.sendEmail && (
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={
                loading ||
                !formData.discrepancyType ||
                !formData.projectId ||
                !formData.sendEmail ||
                !formData.putProjectOnHold ||
                (formData.sendEmail === 'yes' && !formData.emailHtml)
              }
            >
              {formData.sendEmail === 'yes' ? (
                <>
                  <Mail size={16} />
                  {loading ? 'Sending...' : 'Accept and Send'}
                </>
              ) : (
                <>
                  <Send size={16} />
                  {loading ? 'Accepting...' : 'Accept'}
                </>
              )}
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>

    {/* Contact Details Modal */}
    {showContactModal && projectDetails?.contacts && (
      <ContactDetailsModal
        contact={projectDetails.contacts}
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    )}

    {/* New Contact Modal */}
    {showNewContactModal && (
      <NewContactModal
        isOpen={showNewContactModal}
        onClose={() => setShowNewContactModal(false)}
        defaultClientId={projectDetails?.client_id || projectDetails?.clientId}
        zIndex={15000}
        onContactCreated={async (newContact) => {
          // Reload client contacts after creating a new one
          try {
            if (projectDetails?.client_id || projectDetails?.clientId) {
              const { data: contacts } = await supabase
                .from("contacts")
                .select("*")
                .eq("client_id", projectDetails.client_id || projectDetails.clientId)
                .order("name");

              if (contacts) {
                setClientContacts(contacts);
                setShowNewContactModal(false);
              }
            }
          } catch (error) {
            console.error("Error reloading contacts:", error);
            setShowNewContactModal(false);
          }
        }}
      />
    )}

    {/* Toast Notification */}
    {toast.visible && (
      <div
        style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          zIndex: 100000,
          backgroundColor: toast.type === 'success' ? 'var(--success)' : 'var(--danger)',
          color: 'white',
          padding: 'var(--space-4) var(--space-6)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          maxWidth: '400px',
          animation: 'slideInRight 0.3s ease-out',
        }}
      >
        {toast.message}
      </div>
    )}
    </>
  );
};

export default DiscrepancyReviewModal;
